const getMilestonePlanningSummary = (milestone, initiative) => {
  const history = initiative?.history || [];
  const mId = milestone.milestoneNumber;

  // Helper function to get date at UTC midnight (00:00:00)
  const getUTCMidnight = (date) => {
    if (!date) return null;
    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0, 0, 0, 0
    ));
  };

  // 1. Setup Dates - normalize all to UTC midnight
  const now = new Date();
  const today = getUTCMidnight(now);

  const startDate = milestone.startDate ? getUTCMidnight(new Date(milestone.startDate)) : null;
  const endDate = milestone.endDate ? getUTCMidnight(new Date(milestone.endDate)) : null;
  const msInDay = 24 * 60 * 60 * 1000;

  // Calculate days until start and end using Math.ceil for positive differences
  const daysUntilStart = startDate && startDate > today 
    ? Math.ceil((startDate - today) / msInDay) 
    : null;
  
  const daysUntilEnd = endDate && endDate > today 
    ? Math.ceil((endDate - today) / msInDay) 
    : null;

  // 2. Extract Actual Dates from History and normalize to UTC midnight
  const actualStartEntry = history
    .filter(
      h =>
        h.number === mId &&
        h.changes?.status?.previous === 'Not Started' &&
        h.changes?.status?.new === 'Started'
    )
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

  const actualCompletedEntry = history
    .filter(
      h =>
        h.number === mId &&
        h.changes?.status?.previous === 'Started' &&
        h.changes?.status?.new === 'Completed'
    )
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

  const actualStartDate = actualStartEntry
    ? getUTCMidnight(new Date(actualStartEntry.timestamp))
    : null;

  const actualCompletedDate = actualCompletedEntry
    ? getUTCMidnight(new Date(actualCompletedEntry.timestamp))
    : null;

  // Helper function to check if a milestone is "Lately Started"
  const isLatelyStarted = () => {
    return milestone.status === 'Started' && 
           actualStartDate && 
           startDate && 
           actualStartDate > startDate;
  };

  // Helper function to check if a milestone is "Lately Completed"
  const isLatelyCompleted = () => {
    return milestone.status === 'Completed' && 
           actualCompletedDate && 
           endDate && 
           actualCompletedDate > endDate;
  };

  // --- LOGIC ENGINE (Following Priority Rules) ---

  // Priority 8: If milestone is both lately started and lately completed -> Lately Completed
  if (isLatelyStarted() && isLatelyCompleted()) {
    const daysLate = Math.floor((actualCompletedDate - endDate) / msInDay);
    return `Lately Completed (${daysLate} days late)`;
  }

  // Cancelled check
  if (milestone.status === 'Cancelled') return 'Cancelled';
  
  // Not Set check
  if (milestone.status === 'Not Started' && !startDate) return '<not set>';

  // Rule 4 & 7: Completed scenarios
  if (milestone.status === 'Completed') {
    // Rule 6: Lately Completed (completed after end date)
    if (actualCompletedDate && endDate && actualCompletedDate > endDate) {
      const daysLate = Math.floor((actualCompletedDate - endDate) / msInDay);
      return `Lately Completed (${daysLate} days late)`;
    }
    // Rule 4: Completed on time
    // Rule 7: Lately Started but Completed on Time -> Keep Lately Started
    if (isLatelyStarted()) {
      const daysLate = Math.floor((actualStartDate - startDate) / msInDay);
      return `Lately Started (${daysLate} days late)`;
    }
    return 'Completed';
  }

  // Rule 5: Late (Late to Finish) - End date passed and not completed
  if (endDate && today > endDate && milestone.status !== 'Completed') {
    const daysLate = Math.floor((today - endDate) / msInDay);
    return `Late to Finish (${daysLate} days late)`;
  }

  // Rule 3: Started status analysis
  if (milestone.status === 'Started') {
    // FIRST: Check for Due Soon based on end date (within 30 days)
    if (daysUntilEnd !== null && daysUntilEnd <= 30) {
      if (daysUntilEnd <= 7) {
        return 'Due soon';
      }
      return 'Due soon for 30 days';
    }
    
    // SECOND: If end date is more than 30 days away, check for Lately Started
    if (isLatelyStarted()) {
      const daysLate = Math.floor((actualStartDate - startDate) / msInDay);
      return `Lately Started (${daysLate} days late)`;
    }
    
    // THIRD: If end date is more than 30 days away and started on time
    return 'On Plan';
  }

  // Rule 2: Not Started analysis
  if (milestone.status === 'Not Started') {
    // FIRST: Check if end date has passed -> Late to Finish
    if (endDate && today > endDate) {
      const daysLate = Math.floor((today - endDate) / msInDay);
      return `Late to Finish (${daysLate} days late)`;
    }
    
    // SECOND: Check for Due Soon based on start date (within 30 days)
    if (daysUntilStart !== null && daysUntilStart <= 30) {
      if (daysUntilStart <= 7) {
        return 'Due soon';
      }
      return 'Due soon for 30 days';
    }
    
    // THIRD: Check for Late to Start (current date > start date)
    if (startDate && today > startDate) {
      const daysLate = Math.floor((today - startDate) / msInDay);
      return `Late to Start (${daysLate} days late)`;
    }
    
    // FOURTH: On Plan (start date in future > 30 days)
    if (startDate && today < startDate) {
      return 'On Plan';
    }
  }

  return 'On Plan';
};

export default getMilestonePlanningSummary;